/**
 * NATS live price streaming — single WebSocket to streamer.traderverse.io.
 * Uses core NATS subscribe per subject (live ticks) on one connection.
 * Subscriptions are added/removed incrementally; no per-sync JetStream consumer churn.
 */
import { wsconnect } from "@nats-io/nats-core";
import type { NatsConnection, Subscription } from "@nats-io/nats-core";
import { ref } from "vue";
import storageService from "@/services/storage.service";

const NATS_URL =
  process.env.VUE_APP_NATS_URL || "wss://streamer.traderverse.io";

let nc: NatsConnection | null = null;
let connectPromise: Promise<void> | null = null;
const coreSubs = new Map<string, Subscription>();
const subscribedStockIds = new Set<string>();
const isConnected = ref(false);
let onMessageCallback: ((stockId: string, data: any) => void) | null = null;
let syncTimer: ReturnType<typeof setTimeout> | null = null;
let pendingDesiredIds: string[] | null = null;

const toSubject = (stockId: string): string => {
  const id = String(stockId).replace("-USD", "_CRYPTO");
  if (id.endsWith("_CRYPTO")) {
    return `prices.crypto.${id}`;
  }
  return `prices.equity.${id}`;
};

const extractStockId = (subject: string): string => {
  const parts = subject.split(".");
  return parts[parts.length - 1];
};

const dispatchMessage = (subject: string, raw: unknown) => {
  if (!onMessageCallback) return;
  try {
    const data =
      typeof raw === "string"
        ? JSON.parse(raw)
        : raw;
    const stockId = extractStockId(subject);
    onMessageCallback(stockId, data);
  } catch {
    // skip malformed messages
  }
};

const pumpSubscription = (sub: Subscription, subject: string) => {
  (async () => {
    try {
      for await (const msg of sub) {
        dispatchMessage(subject, msg.json());
      }
    } catch {
      // subscription closed
    }
  })();
};

const subscribeSubject = (subject: string) => {
  if (!nc || nc.isClosed() || coreSubs.has(subject)) return;
  const sub = nc.subscribe(subject);
  coreSubs.set(subject, sub);
  pumpSubscription(sub, subject);
};

const unsubscribeSubject = (subject: string) => {
  const sub = coreSubs.get(subject);
  if (!sub) return;
  try {
    sub.unsubscribe();
  } catch {
    // already closed
  }
  coreSubs.delete(subject);
};

const applyCoreSubscriptions = (desiredStockIds: string[]) => {
  if (!nc || nc.isClosed()) return;

  const desiredSubjects = new Set(desiredStockIds.map(toSubject));

  for (const subject of Array.from(coreSubs.keys())) {
    if (!desiredSubjects.has(subject)) {
      unsubscribeSubject(subject);
    }
  }

  for (const subject of Array.from(desiredSubjects)) {
    subscribeSubject(subject);
  }

  subscribedStockIds.clear();
  desiredStockIds.forEach((id) => subscribedStockIds.add(id));
};

const monitorConnection = () => {
  if (!nc) return;

  (async () => {
    for await (const s of nc!.status()) {
      switch (s.type) {
        case "disconnect":
          isConnected.value = false;
          break;
        case "reconnect":
          isConnected.value = true;
          if (subscribedStockIds.size > 0) {
            applyCoreSubscriptions(Array.from(subscribedStockIds));
          }
          break;
        case "error":
          break;
      }
    }
  })();

  nc.closed().then(() => {
    isConnected.value = false;
    coreSubs.clear();
    subscribedStockIds.clear();
    nc = null;
    connectPromise = null;
  });
};

export const useNatsStreamer = () => {
  const getToken = (): string => storageService.getToken() || "";

  const connectToNats = async (token?: string): Promise<void> => {
    if (nc && !nc.isClosed()) return;
    if (connectPromise) return connectPromise;

    const authToken = token || getToken();
    if (!authToken) return;

    connectPromise = (async () => {
      try {
        nc = await wsconnect({
          servers: [NATS_URL],
          token: authToken,
        });
        isConnected.value = true;
        monitorConnection();
      } catch {
        isConnected.value = false;
        nc = null;
      } finally {
        connectPromise = null;
      }
    })();

    return connectPromise;
  };

  const flushSync = async () => {
    syncTimer = null;
    const ids = pendingDesiredIds ?? [];
    pendingDesiredIds = null;
    if (!ids.length) return;

    await connectToNats();
    if (!nc || nc.isClosed()) return;

    applyCoreSubscriptions(ids);
  };

  const syncSubscriptions = async (
    desiredStockIds: string[],
    onMessage: (stockId: string, data: any) => void
  ) => {
    onMessageCallback = onMessage;
    pendingDesiredIds = desiredStockIds;

    const desired = new Set(desiredStockIds);
    if (
      desired.size === subscribedStockIds.size &&
      Array.from(desired).every((id) => subscribedStockIds.has(id))
    ) {
      return;
    }

    if (syncTimer) clearTimeout(syncTimer);
    syncTimer = setTimeout(() => {
      flushSync().catch(() => {});
    }, 400);
  };

  const subscribe = async (
    stockIds: string[],
    onMessage: (stockId: string, data: any) => void
  ) => {
    const merged = Array.from(
      new Set([...Array.from(subscribedStockIds), ...stockIds])
    );
    await syncSubscriptions(merged, onMessage);
  };

  const unsubscribe = async (stockIds: string[]) => {
    const remaining = Array.from(subscribedStockIds).filter(
      (id) => !stockIds.includes(id)
    );
    if (onMessageCallback) {
      await syncSubscriptions(remaining, onMessageCallback);
    } else {
      pendingDesiredIds = remaining;
      await flushSync();
    }
  };

  const unsubscribeAll = async () => {
    if (syncTimer) {
      clearTimeout(syncTimer);
      syncTimer = null;
    }
    pendingDesiredIds = null;
    for (const subject of Array.from(coreSubs.keys())) {
      unsubscribeSubject(subject);
    }
    subscribedStockIds.clear();
  };

  const disconnect = async () => {
    await unsubscribeAll();
    if (nc && !nc.isClosed()) {
      await nc.drain();
    }
    nc = null;
    isConnected.value = false;
    connectPromise = null;
  };

  const getSubscribedStockIds = (): string[] => Array.from(subscribedStockIds);

  return {
    isConnected,
    connectToNats,
    subscribe,
    syncSubscriptions,
    unsubscribe,
    unsubscribeAll,
    disconnect,
    getSubscribedStockIds,
  };
};

/** Debounced NATS resubscribe when the visible symbol set changes. */
let natsScheduleTimer: ReturnType<typeof setTimeout> | null = null;
const natsStreamerSingleton = useNatsStreamer();

export const scheduleNatsSync = (
  getDesiredIds: () => string[],
  onMessage: (stockId: string, data: any) => void
) => {
  if (natsScheduleTimer) clearTimeout(natsScheduleTimer);
  natsScheduleTimer = setTimeout(() => {
    natsScheduleTimer = null;
    const token = storageService.getToken();
    if (!token) return;
    const ids = getDesiredIds().filter(Boolean);
    if (!ids.length) return;
    natsStreamerSingleton
      .connectToNats(token)
      .then(() => natsStreamerSingleton.syncSubscriptions(ids, onMessage))
      .catch(() => {});
  }, 400);
};
